import React, {useEffect, useState} from 'react';
import './ConfirmationWindow.scss';
import {MessageCircle, Voicemail, Contact2, X, Sparkles, Languages, User, Check, Trash2} from 'lucide-react'
import {AudioWaveform} from "../../ui/AudioWaveform/AudioWaveform ";
import Button from "../../ui/Button/Button";
import AgreementCheckbox from "../../ui/AgreementCheckbox/AgreementCheckbox";
import {NewChatData} from "../../../store";
import Telegram from '../../../pages/CreateChat/Steps/telegram.png';
import WhatsApp from '../../../pages/CreateChat/Steps/whatsapp.png';
import {useObjectUrl} from "../../../hooks/useObjectUrl";

const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface CreateChatConfirmProps {
    newChatData: NewChatData;
    confirmAction: () => void;
    closeMenu: () => void;
}

const CreateChatConfirm = ({newChatData, confirmAction, closeMenu}: CreateChatConfirmProps) => {
    const [consentGiven, setConsentGiven] = useState(false);
    const avatarUrl = useObjectUrl(newChatData.chat_avatar);
    const voiceUrl = useObjectUrl(newChatData.voice_sample);
    const person = newChatData.person_to_copy;

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeMenu();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [closeMenu]);

    return (
        <div className="ConfirmWindow" role="dialog" aria-modal="true" aria-labelledby="createConfirmTitle">
            <div className="ConfirmWindow__blur" onClick={closeMenu}></div>

            <div className="ConfirmWindow__form CreateConfirm">
                <button type="button" className="CreateConfirm__close" aria-label="Close" onClick={closeMenu}>
                    <X size={18}/>
                </button>

                <div className="CreateConfirm__hero">
                    <div className="CreateConfirm__avatar">
                        {avatarUrl && <img src={avatarUrl} alt=""/>}
                    </div>
                    <span className="CreateConfirm__eyebrow"><Sparkles size={14}/> Almost ready</span>
                    <h2 id="createConfirmTitle">{newChatData.chat_name}</h2>
                    <p>AI clone of <b>{person}</b></p>

                    <div className="CreateConfirm__chips">
                        <span className="CreateConfirm__chip">
                            <img src={newChatData.chat_type === 'Telegram' ? Telegram : WhatsApp} alt=""/>
                            {newChatData.chat_type}
                        </span>
                        <span className="CreateConfirm__chip">
                            <Languages size={14}/> {newChatData.chat_language?.name}
                        </span>
                        <span className="CreateConfirm__chip CreateConfirm__chip--capitalize">
                            <User size={14}/> {newChatData.chat_gender}
                        </span>
                    </div>
                </div>

                <div className="CreateConfirm__body">
                    <section className="CreateConfirm__card">
                        <div className="CreateConfirm__cardIcon"><MessageCircle size={18}/></div>
                        <div className="CreateConfirm__cardContent">
                            <h4>Chat history</h4>
                            <span className="CreateConfirm__fileName">{newChatData.chat_file?.name}</span>
                            {newChatData.chat_file &&
                                <span className="CreateConfirm__meta">{formatFileSize(newChatData.chat_file.size)}</span>}
                        </div>
                    </section>

                    <section className="CreateConfirm__card">
                        <div className="CreateConfirm__cardIcon"><Voicemail size={18}/></div>
                        <div className="CreateConfirm__cardContent">
                            <h4>Voice sample</h4>
                            {voiceUrl && <AudioWaveform audioSrc={voiceUrl}/>}
                        </div>
                    </section>

                    <section className="CreateConfirm__card">
                        <div className="CreateConfirm__cardIcon"><Contact2 size={18}/></div>
                        <div className="CreateConfirm__cardContent">
                            <h4>Personality source</h4>
                            <span className="CreateConfirm__meta">Replies will copy how {person} writes</span>
                            <div className="CreateConfirm__members">
                                {newChatData.chat_members?.map((member) => (
                                    <span key={member}
                                          className={`CreateConfirm__member ${member === person ? 'CreateConfirm__member--active' : ''}`}>
                                        {member === person && <Check size={14}/>}
                                        {member}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>

                <div className="CreateConfirm__consent">
                    <AgreementCheckbox checked={consentGiven} onChange={setConsentGiven}>
                        I confirm that I have the right to use this chat history, voice sample and photo,
                        and that {person || 'this person'} has consented, or has passed
                        away and I am a close family member. I will not use this AI simulation to impersonate
                        them. <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                    </AgreementCheckbox>
                </div>

                <div className="CreateConfirm__footer">
                    <Button type={'button'} onClick={closeMenu} variant={'secondary'} title={'Cancel'}/>
                    <Button type={'button'} disabled={!consentGiven} onClick={confirmAction} variant={'primary'}
                            title={'Create clone'} icon={<Sparkles size={16}/>}/>
                </div>
            </div>
        </div>
    );
};

const ConfirmWindow = ({newChatData, confirmAction, closeMenu, type, chatName}: any) => {

    return type === 'confirm' ? (
        <div className="ConfirmWindow" role="dialog" aria-modal="true" aria-labelledby="deleteConfirmTitle">
            <div className="ConfirmWindow__blur" onClick={closeMenu}></div>

            <div className="ConfirmWindow__form deleteForm">
                <div className="icon-tile deleteForm__icon"><Trash2 size={22}/></div>
                <h2 id="deleteConfirmTitle">Delete {chatName ? `“${chatName}”` : 'this chat'}?</h2>
                <p>This permanently deletes the chat, all of its messages and the cloned voice. You can't undo this.</p>
                <div className="row buttonsRow">
                    <Button type={'button'} onClick={closeMenu} variant={'secondary'} title={'Cancel'}/>
                    <Button type={'button'} onClick={confirmAction} variant={'danger'} title={'Delete chat'}/>
                </div>

            </div>
        </div>
    ) : <CreateChatConfirm newChatData={newChatData} confirmAction={confirmAction} closeMenu={closeMenu}/>;
};

export default ConfirmWindow;
